import { withoutForbiddenXmlCharacters } from '@eformulieren/model'
import { type ReactNode, type RefObject, useLayoutEffect, useRef } from 'react'
import { Counter } from '../Counter.js'
import { type SingleProps, type TextProps, asText } from './kinds.js'
import { useTextBinding } from './textBinding.js'
import { RefusedCharactersNotice, useRefusedCharacters } from './refusedCharacters.js'

/**
 * Meerdere regels tekst zonder opmaak: `textarea` (besluit van 27 oktober 2026).
 *
 * De CitatenPOC, de maatstaf voor het gedrag van de controls (eisenboek 12.9), kent naast tekst en
 * rich text een meerregelig veld. Dit is dat veld: dezelfde antwoordvorm als `text` (één waarde, een
 * tekst), dezelfde teller en dezelfde regels, alleen een vak met meerdere regels in plaats van één.
 * Een regeleinde is gewoon een teken in de waarde; de alleen-lezen weergave toont het als nieuwe
 * alinea (`dossierValue.ts`).
 *
 * **Een `textarea` en geen `contenteditable`.** Het element is labelbaar, dus de kop komt via
 * `label for` zoals bij `text` en niet via `aria-labelledby` (`HEADER_VIA_ARIA` in de registry), en
 * toetsenbord en schermlezer krijgen het gedrag van de browser zonder dat er iets na te bouwen is
 * (R-139). Tab gaat naar het volgende veld; Enter is een nieuwe regel.
 *
 * **Geen `maxlength` bij een tekenlimiet**, om dezelfde reden als bij `TextLine`: de browser kapt
 * dan stil af en de teller kan de overschrijding niet meer laten zien (R-12, R-13).
 *
 * **Samenvoegen per teken** (R-72). In het formulier staat de tekst als `Y.Text` in het
 * sectiedocument (`vorm: 'tekst'`), en twee medeschrijvers die tegelijk in hetzelfde vak typen
 * zien beide hun tekst terug (`useTextBinding`). Wie het vak zonder document tekent kan het nog
 * steeds een waarde geven, net als `TextLine`.
 */

/** Het aantal zichtbare regels waarmee het vak begint; de invuller kan het groter trekken. */
const ROWS = 5

/**
 * Een antwoord dat de lezer niet kan bewerken krijgt de hoogte van zijn tekst (R-133). Met vijf
 * vaste regels stond onder een kort antwoord een lege ruimte, en een lang antwoord werd afgekapt
 * binnen een vak zonder rand. Gemeten en niet met `field-sizing`, want dat kent alleen Chromium;
 * opnieuw bij elke tekst en bij elke breedte, want een smallere kolom maakt meer regels.
 */
function useFitHeight(ref: RefObject<HTMLTextAreaElement | null>, active: boolean, text: string): void {
  useLayoutEffect(() => {
    const element = ref.current
    if (element === null) return undefined
    if (!active) {
      element.style.height = ''
      return undefined
    }
    const fit = (): void => {
      element.style.height = 'auto'
      element.style.height = `${String(element.scrollHeight)}px`
    }
    fit()
    // Na een breedtewijziging pas in het volgende frame passen, niet in de melding zelf: `fit`
    // verandert de hoogte van het element dat wordt waargenomen, en een maatwijziging binnen de
    // eigen melding laat de browser "ResizeObserver loop completed with undelivered notifications"
    // melden, als fout op het venster.
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(fit)
    })
    observer.observe(element)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [ref, active, text])
}

export function TextArea(props: SingleProps | TextProps): ReactNode {
  return props.vorm === 'tekst' ? <BoundTextArea {...props} /> : <ValueTextArea {...props} />
}

function describedBy(link: SingleProps['koppeling'], counterId: string | undefined): string | undefined {
  const description = [link['aria-describedby'], counterId]
    .filter((part): part is string => part !== undefined)
    .join(' ')
  return description === '' ? undefined : description
}

/** Het vak aan een `Y.Text`: de tekst komt uit het document en gaat er per teken in terug. */
function BoundTextArea({
  taal: language,
  koppeling: link,
  tekst: text,
  onOwnChange,
  bewerkbaar: editable,
  limieten: limits,
}: TextProps): ReactNode {
  const { refused, report } = useRefusedCharacters()
  const { ref, current } = useTextBinding<HTMLTextAreaElement>(text, { editable, onOwnChange, onFiltered: report })
  useFitHeight(ref, !editable, current)
  const counterId = `${link.id}-teller`
  return (
    <>
      <textarea
        ref={ref}
        className="ef-invoer ef-invoer--meerregelig"
        rows={editable ? ROWS : 1}
        {...link}
        aria-describedby={describedBy(link, limits === undefined ? undefined : counterId)}
        defaultValue={current}
        readOnly={!editable}
      />
      <Counter tekst={current} limits={limits} taal={language} id={counterId} />
      <RefusedCharactersNotice refused={refused} taal={language} />
    </>
  )
}

function ValueTextArea({
  taal: language,
  koppeling: link,
  waarde: value,
  onWaarde: onValue,
  bewerkbaar: editable,
  limieten: limits,
}: SingleProps): ReactNode {
  const textValue = asText(value)
  const { refused, report } = useRefusedCharacters()
  const ref = useRef<HTMLTextAreaElement | null>(null)
  useFitHeight(ref, !editable, textValue)
  const counterId = `${link.id}-teller`
  const description = [link['aria-describedby'], limits === undefined ? undefined : counterId]
    .filter((part): part is string => part !== undefined)
    .join(' ')

  return (
    <>
      <textarea
        ref={ref}
        className="ef-invoer ef-invoer--meerregelig"
        rows={editable ? ROWS : 1}
        {...link}
        aria-describedby={description === '' ? undefined : description}
        value={textValue}
        readOnly={!editable}
        onChange={(event) => {
          // Zonder de tekens die niet kunnen worden verstuurd, zoals bij de binding (`textBinding.ts`).
          const typed = event.target.value
          const createNew = withoutForbiddenXmlCharacters(typed)
          report(createNew !== typed)
          // Leeg is leeg, zoals bij `TextLine`: anders slaagt `verplicht` op een leeg vak (R-26).
          onValue(createNew === '' ? undefined : createNew)
        }}
      />
      <Counter tekst={textValue} limits={limits} taal={language} id={counterId} />
      <RefusedCharactersNotice refused={refused} taal={language} />
    </>
  )
}
